import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { COMPANY, NAV } from "../../lib/constants";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0a0a0c]/85 backdrop-blur-xl border-b border-zinc-800/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
          aria-label="RevMatrix home"
        >
          <img
            src={COMPANY.logoUrl}
            alt="RevMatrix"
            className="h-10 w-10 object-contain"
            data-testid="header-logo-img"
          />
          <span className="font-display text-xl font-extrabold tracking-tight">
            <span className="text-white">Rev</span>
            <span className="text-violet-500">Matrix</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-violet-400"
                    : "text-zinc-300 hover:text-white"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone-link"
            className="text-sm font-mono text-zinc-400 hover:text-white transition-colors"
          >
            {COMPANY.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="rm-btn-primary text-sm"
          >
            Get in touch <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="header-mobile-toggle"
          className="md:hidden inline-flex items-center justify-center h-10 w-10 border border-zinc-800 text-white"
          aria-label="Toggle navigation"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-nav-panel"
          className="md:hidden border-t border-zinc-800/70 bg-[#0a0a0c]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base font-medium py-2 ${
                    isActive ? "text-violet-400" : "text-zinc-200"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="h-px bg-zinc-800 my-2" />
            <a
              href={COMPANY.phoneHref}
              data-testid="mobile-phone-link"
              className="text-sm font-mono text-zinc-400"
            >
              {COMPANY.phoneDisplay}
            </a>
            <Link
              to="/contact"
              data-testid="mobile-cta-contact"
              className="rm-btn-primary justify-center"
            >
              Get in touch <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
